import {gql} from '@apollo/client';
import {objectFromEntries, compact} from '@benchling/js-utils';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {
  Container,
  isInaccessibleStorable,
  Plate,
  SampleDetails,
  SampleDetailsWithSchema,
} from 'constants/SampleConstants';
import SampleType from 'constants/SampleType';
import {BatchInfo, BioentityInfo} from 'folder-items/FileConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {restToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import {ManagedModelType, ManagedModelTypes} from 'models/ManagedModelDefinitions';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import notifications from 'notifications';
import {OpenCreateModalArgs} from 'requests/dashboard/openCreateRequestModal';
import {RequestSchema} from 'requests/RequestConstants';
import {makeStageRunSample, updateSampleGroupWithSample} from 'requests/samples-table/RequestSampleTableUtil';
import getInitialRequestProps from 'requests/templates/getInitialRequestProps';
import {InvalidPositionError, parseGridPosition} from 'samples/SampleUtil';
import {makeSampleDetailsFromSamples} from 'samples/StageRunSampleModelManager';
import {PlateSchema, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import {isSampleFieldForBioentity} from 'schemas/fields/SchemaFieldConstants';
import {isTagSchema} from 'schemas/SchemaPredicates';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import memoizeLastCall from 'util/memoizeLastCall';
import makeSampleGroup from 'workflows/samples/makeSampleGroup';

import {RequestSchemaSelectionModalQuery} from './__generated/RequestSchemaSelectionModalQuery';
import {SampleLookupInfoForCreateRequest} from './CreateRequestFromSamplesButton';
import RequestSchemaSelector from './RequestSchemaSelector';

export interface RequestSchemaSelectionModalProps {
  sampleInfos?: Array<SampleLookupInfoForCreateRequest>;
  samples?: Array<SampleDetailsWithSchema>;
  onRequestClose: () => void;
  onRequestSubmit: (openModalArgs: OpenCreateModalArgs) => void;
  rootFolderId?: string;
  noSamplesMessage?: string;
  isLoading?: boolean;
}

interface ConnectedProps {
  samples: Array<SampleDetailsWithSchema>;
  sampleTypes: Set<SampleType>;
  sampleSchemaIds: Set<string>;
  tooltipMessage?: string;
}

interface SamplesFromSampleInfos {
  samples: Array<SampleDetailsWithSchema>;
  invalidSamplesMessage?: string; // A user-visible description of why the given samples are invalid.
}

export const REQUEST_SCHEMA_SELECTION_MODAL_QUERY = gql`
  query RequestSchemaSelectionModalQuery @skipAudit @engComponent(name: REQUESTS) {
    ...AllRequestTaskSchemasGraphQL
    ...AllRequestTemplatesGraphQL
  }
  ${getFragmentWrapper('AllRequestTaskSchemas').fragment}
  ${getFragmentWrapper('AllRequestTemplates').fragment}
`;

export function isRequestSchemaValid({
  requestSchema,
  sampleTypes,
  sampleSchemaIds,
}: {
  requestSchema: RequestSchema;
  sampleTypes: Set<SampleType>;
  sampleSchemaIds: Set<string>;
}): boolean {
  if (requestSchema.sampleGroupSchema == null) {
    return false;
  }
  if (requestSchema.sampleGroupSchema.fields.length !== 1) {
    return false;
  }
  const sampleField = requestSchema.sampleGroupSchema.fields[0];
  if (!sampleTypes.has(sampleField.requiredLink.sampleType)) {
    return false;
  }
  const requiredSchema = isSampleFieldForBioentity(sampleField)
    ? sampleField.requiredLink.tagSchema
    : sampleField.requiredLink.batchSchema;
  if (requiredSchema != null && !sampleSchemaIds.has(requiredSchema.id)) {
    return false;
  }
  return true;
}

export function getSampleSchemaIds(samples: Array<SampleDetailsWithSchema>): Set<string> {
  // SampleSchemaIds are the id of a TagSchema or a BatchSchema, depending on the sample.
  return new Set(
    compact(
      samples.map((sample) => {
        if (!sample.schema) {
          return null;
        }
        if (sample.batchInfo != null) {
          return isTagSchema(sample.schema) ? sample.schema.batchSchemaId : sample.schema.id;
        }
        return sample.schema.id;
      })
    )
  );
}

export function getSampleTypes(samples: Array<SampleDetailsWithSchema>): Set<SampleType> {
  return new Set(
    samples
      .map((sample) => {
        if (sample.bioentityInfo != null) {
          return SampleType.BIOENTITY;
        }
        if (sample.batchInfo != null) {
          return SampleType.BATCH;
        }
        return null;
      })
      .filter((sampleType) => sampleType != null)
  ) as Set<SampleType>;
}

export function hydrateSampleDetailsWithPlateInfo(
  samples: Array<SampleDetailsWithSchema>,
  wellIdToPlate: {[wellId: string]: Plate}
): Array<SampleDetailsWithSchema> {
  return samples.map((sampleDetails) => {
    assert(sampleDetails.containerInfo != null, 'SampleDetails for plate well should have a container');
    const wellId = sampleDetails.containerInfo.id;
    const plate = wellIdToPlate[wellId];
    assert(plate != null, 'Could not find plate for SampleDetails plate well');
    return {
      ...sampleDetails,
      plateInfo: {
        id: plate.id,
        name: plate.name,
        barcode: plate.barcode,
      },
    };
  });
}

class RequestSchemaSelectionModalDataConnector extends DataConnector<
  RequestSchemaSelectionModalProps,
  ConnectedProps
> {
  getContainerIds = memoizeLastCall((samples: Array<SampleDetailsWithSchema>) =>
    samples.flatMap((sample) => (sample.containerInfo != null ? [sample.containerInfo.id] : []))
  );
  getPlateIds = memoizeLastCall((samples: Array<SampleDetailsWithSchema>) =>
    samples.flatMap((sample) => (sample.plateInfo != null ? [sample.plateInfo.id] : []))
  );
  getSampleTypes = memoizeLastCall((samples: Array<SampleDetailsWithSchema>) => getSampleTypes(samples));
  getSampleSchemaIds = memoizeLastCall((samples: Array<SampleDetailsWithSchema>) =>
    getSampleSchemaIds(samples)
  );
  _getIdsToFetch(): {
    containerIds: Array<string>;
    bioentityIds: Array<string>;
    batchIds: Array<string>;
    allWellsPlateIds: Array<string>;
    plateWellSampleInfos: Array<SampleLookupInfoForCreateRequest>;
    invalidSamplesMessage?: string;
  } {
    const batchIds = [];
    const bioentityIds = [];
    const containerIds = [];
    const allWellsPlateIds = [];
    const plateWellSampleInfos: Array<SampleLookupInfoForCreateRequest> = [];
    for (const sampleInfo of this.props.sampleInfos!) {
      const {idForLookup, plateWellPositionString} = sampleInfo;
      if (APIUtil.isBatch(idForLookup)) {
        batchIds.push(idForLookup);
      } else if (APIUtil.isBiologicalEntity(idForLookup)) {
        bioentityIds.push(idForLookup);
      } else if (APIUtil.isContainer(idForLookup)) {
        containerIds.push(idForLookup);
      } else if (APIUtil.isPlate(idForLookup)) {
        if (plateWellPositionString == null) {
          allWellsPlateIds.push(idForLookup);
        } else {
          plateWellSampleInfos.push(sampleInfo);
        }
      } else {
        logging.error(
          `Tried to create a request for ${idForLookup} which is neither a batch, entity, plate, or container.`
        );
        return {
          batchIds,
          bioentityIds,
          containerIds,
          plateWellSampleInfos,
          allWellsPlateIds,
          invalidSamplesMessage: `Error loading samples. ${idForLookup} is not supported.`,
        };
      }
    }
    return {batchIds, bioentityIds, containerIds, plateWellSampleInfos, allWellsPlateIds};
  }

  _fetchBioentitySamples(bioentityIds: Array<string>): SamplesFromSampleInfos {
    const bioentitiesById = this.getOrFetchMultipleRequired(ManagedModelType.BIOENTITY_SUMMARY, bioentityIds);
    const bioentities = bioentitiesById ? Object.values(bioentitiesById) : [];
    if (bioentities.length === 0) {
      return {samples: []};
    }
    const samples = [];
    for (const bioentity of bioentities) {
      samples.push({
        ...makeSampleDetailsFromSamples({
          batch: null,
          bioentitySummary: bioentity,
          container: null,
          plate: null,
        }),
        schema: bioentity.tagSchema,
      });
    }
    return {samples};
  }

  _fetchBatchSamples(batchIds: Array<string>): SamplesFromSampleInfos {
    const batchesById = this.getOrFetchMultipleRequired(ManagedModelType.BATCH, batchIds);
    const batches = batchesById ? Object.values(batchesById) : [];
    if (batches.length === 0) {
      return {samples: []};
    }
    const samples = [];
    for (const batch of batches) {
      samples.push({
        ...makeSampleDetailsFromSamples({
          batch,
          bioentitySummary: null,
          container: null,
          plate: null,
        }),
        schema: batch.file.tagSchema, // This is intentionally not the batchSchema.
      });
    }
    return {samples};
  }

  _fetchSamplesFromContainers(containerIds: Array<string>): SamplesFromSampleInfos {
    const containersById = this.getOrFetchMultipleRequired(ManagedModelType.CONTAINER, containerIds);
    const containers = containersById ? Object.values(containersById) : [];
    if (containers.length === 0) {
      return {samples: []};
    }

    const batchIds: Array<string> = [];
    const bioentityIds: Array<string> = [];
    const bioentityOrBatchIdToContainers = new Map<string, Array<Container>>();
    for (const container of containers) {
      if (container.contents.length === 0) {
        return {
          samples: [],
          invalidSamplesMessage: `Empty container ${container.name} is not supported`,
        };
      } else if (container.contents.length > 1) {
        return {
          samples: [],
          invalidSamplesMessage: `Container ${container.name} with multiple contents is not supported`,
        };
      }
      for (const {containedItemId} of container.contents) {
        let mappedContainers = bioentityOrBatchIdToContainers.get(containedItemId);
        if (!mappedContainers) {
          mappedContainers = [];
          bioentityOrBatchIdToContainers.set(containedItemId, mappedContainers);
        }
        mappedContainers.push(container);
        if (APIUtil.isBatch(containedItemId)) {
          batchIds.push(containedItemId);
        } else if (APIUtil.isBiologicalEntity(containedItemId)) {
          bioentityIds.push(containedItemId);
        } else {
          logging.warn(
            `Tried to create a request with non-empty sample ${container.id} which does not contain a batch or entity.`
          );
          return {
            samples: [],
            invalidSamplesMessage: `Container ${container.name} is not supported`,
          };
        }
      }
    }

    if (batchIds.length > 0 && bioentityIds.length > 0) {
      // Only support samples all of one sampleType.
      return {
        samples: [],
        invalidSamplesMessage: `Container contents must all be entities or all be batches`,
      };
    }

    const {samples} =
      bioentityIds.length > 0 ? this._fetchBioentitySamples(bioentityIds) : this._fetchBatchSamples(batchIds);
    const samplesWithContainers = [];
    for (const sample of samples) {
      let id = '';
      if (sample.bioentityInfo) {
        id = sample.bioentityInfo.id;
      } else if (sample.batchInfo?.id != null) {
        id = sample.batchInfo.id;
      }
      if (id) {
        const mappedContainers = bioentityOrBatchIdToContainers.get(id);
        if (mappedContainers) {
          for (const container of mappedContainers) {
            const containerInfo = {
              id: container.id,
              name: container.id,
              barcode: container.barcode,
              gridCell: container.gridCell,
            };
            samplesWithContainers.push({...sample, containerInfo});
          }
        }
      }
    }

    return {samples: samplesWithContainers};
  }

  _getWellIdToPlate(
    fetchFn: (
      modelType: ManagedModelType.STORABLE,
      keys: Array<string>
    ) => {[key: string]: ManagedModelTypes[ManagedModelType.STORABLE]},
    samplePlateWellInfos: Array<SampleLookupInfoForCreateRequest>,
    allWellsPlateIds: Array<string>
  ): {[wellId: string]: Plate} {
    const platesById = fetchFn(ManagedModelType.STORABLE, [
      ...samplePlateWellInfos.map((sampleInfo) => sampleInfo.idForLookup),
      ...allWellsPlateIds,
    ]);
    const samplePlateWellInfosPlates: Array<Plate | undefined> = platesById
      ? (samplePlateWellInfos.map((sampleInfo) => platesById[sampleInfo.idForLookup]) as Array<
          Plate | undefined
        >)
      : [];
    const wellIdToPlate: {[wellId: string]: Plate} = {};
    // Get specified well containers from samplePlateWellInfos
    for (const [idx, plate] of samplePlateWellInfosPlates.entries()) {
      if (plate != null) {
        const positionString = samplePlateWellInfos[idx].plateWellPositionString;
        const plateSchema: PlateSchema = plate.storableSchema;
        let rowIndex;
        let columnIndex;
        try {
          ({rowIndex, columnIndex} = parseGridPosition({
            position: positionString,
            gridHeight: plateSchema.height,
            gridWidth: plateSchema.width,
          }));
        } catch (e) {
          if (e instanceof InvalidPositionError) {
            throw new Error('Position should be valid. Sample has already been transferred to this position');
          } else {
            throw e;
          }
        }

        const cell = plate.cells[rowIndex][columnIndex];
        if (cell == null) {
          // If specified position is empty in plate, container must've been moved out of matrix plate.
          assert(plate.storableSchema.plateSchemaType === PlateSchemaType.MATRIX_PLATE, '');
          continue;
        }
        if (isInaccessibleStorable(cell.container)) {
          continue;
        }
        // Extract container contents
        wellIdToPlate[cell.container.id] = plate;
      }
    }
    // Get all well containers from plates in allWellsPlateIds
    const allWellsPlates: Array<Plate | undefined> = platesById
      ? (allWellsPlateIds.map((plateId) => platesById[plateId]) as Array<Plate | undefined>)
      : [];
    for (const plate of allWellsPlates) {
      if (plate != null) {
        for (const cell of plate.cells.flat()) {
          if (cell != null && !isInaccessibleStorable(cell.container)) {
            wellIdToPlate[cell.container.id] = plate;
          }
        }
      }
    }
    return wellIdToPlate;
  }

  _fetchSamplesFromPlateWells(
    samplePlateWellInfos: Array<SampleLookupInfoForCreateRequest>,
    allWellsPlateIds: Array<string>
  ): SamplesFromSampleInfos {
    const wellIdToPlate: {[wellId: string]: Plate} = this._getWellIdToPlate(
      (modelType, keys) => this.getOrFetchMultipleRequired(modelType, keys),
      samplePlateWellInfos,
      allWellsPlateIds
    );
    if (Object.entries(wellIdToPlate).length === 0) {
      return {samples: []};
    }
    const containerSampleDetails = this._fetchSamplesFromContainers(Array.from(Object.keys(wellIdToPlate)));
    return {
      ...containerSampleDetails,
      samples: hydrateSampleDetailsWithPlateInfo(containerSampleDetails.samples, wellIdToPlate),
    };
  }

  _fetchSamplesFromSampleInfos(): SamplesFromSampleInfos {
    if (this.props.sampleInfos!.length === 0) {
      return {
        samples: [],
        invalidSamplesMessage: this.props.noSamplesMessage,
      };
    }

    let samples: Array<SampleDetailsWithSchema> = [];
    const {
      containerIds,
      bioentityIds,
      batchIds,
      allWellsPlateIds,
      plateWellSampleInfos,
      invalidSamplesMessage,
    } = this._getIdsToFetch();
    if (invalidSamplesMessage != null) {
      return {samples: [], invalidSamplesMessage};
    }
    if (
      [containerIds, bioentityIds, batchIds, allWellsPlateIds, plateWellSampleInfos].every(
        (list) => list.length === 0
      )
    ) {
      return {samples, invalidSamplesMessage: 'Error loading samples'};
    }

    if (containerIds.length > 0) {
      const containerSampleData = this._fetchSamplesFromContainers(containerIds);
      if (containerSampleData.invalidSamplesMessage != null) {
        return containerSampleData;
      }
      samples = [...samples, ...containerSampleData.samples];
    }
    if (bioentityIds.length > 0) {
      const bioentitySampleData = this._fetchBioentitySamples(bioentityIds);
      if (bioentitySampleData.invalidSamplesMessage != null) {
        return bioentitySampleData;
      }
      samples = [...samples, ...bioentitySampleData.samples];
    }
    if (batchIds.length > 0) {
      const batchSampleData = this._fetchBatchSamples(batchIds);
      if (batchSampleData.invalidSamplesMessage != null) {
        return batchSampleData;
      }
      samples = [...samples, ...batchSampleData.samples];
    }
    if (plateWellSampleInfos.length > 0) {
      const plateSampleData = this._fetchSamplesFromPlateWells(plateWellSampleInfos, allWellsPlateIds);
      if (plateSampleData.invalidSamplesMessage != null) {
        return plateSampleData;
      }
      samples = [...samples, ...plateSampleData.samples];
    }
    return {
      samples,
    };
  }

  override renderLoading(): React.ReactNode {
    return (
      <RequestSchemaSelectionModal
        isLoading={true}
        {...this.props}
        samples={[]}
        sampleTypes={new Set()}
        sampleSchemaIds={new Set()}
      />
    );
  }

  enhanceProps(): ConnectedProps {
    let createRequestSamples = this.props.samples ?? [];
    const containerIds = this.getContainerIds(createRequestSamples);
    const plateIds = this.getPlateIds(createRequestSamples);
    // So that the Create Request modal will know what plate each well is in
    if (containerIds.length > 0 && !StorableSummaryExtrasManager.areKeysLoadedOrLoading(containerIds)) {
      StorableSummaryExtrasManager.fetchMulti(containerIds);
    }
    if (plateIds.length > 0 && !StorableSummaryExtrasManager.areKeysLoadedOrLoading(plateIds)) {
      StorableSummaryExtrasManager.fetchMulti(plateIds);
    }
    let tooltipMessage: string | undefined;
    if (this.props.samples == null && this.props.sampleInfos != null) {
      const {samples, invalidSamplesMessage} = this._fetchSamplesFromSampleInfos();
      createRequestSamples = samples;
      tooltipMessage = invalidSamplesMessage;
    }
    const sampleTypes = this.getSampleTypes(createRequestSamples);
    const sampleSchemaIds = this.getSampleSchemaIds(createRequestSamples);

    return {
      samples: createRequestSamples,
      sampleTypes,
      sampleSchemaIds,
      tooltipMessage,
    };
  }
}

function RequestSchemaSelectionModal({
  rootFolderId,
  onRequestClose,
  samples,
  sampleSchemaIds,
  sampleTypes,
  tooltipMessage,
  onRequestSubmit,
  isLoading,
}: RequestSchemaSelectionModalProps & ConnectedProps): JSX.Element | null {
  const [requestSchema, setRequestSchema] = useState<RequestSchema | null>(null);
  const result = useBenchlingQuery<RequestSchemaSelectionModalQuery>(REQUEST_SCHEMA_SELECTION_MODAL_QUERY);
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load request details: try reloading the page.');
    return null;
  } else if (isLoading || result.type === QueryResultType.PENDING) {
    return (
      <Modal title="Loading..." onRequestClose={() => {}} buttons={[]}>
        <CenteredHorizontalThrobber />
      </Modal>
    );
  } else {
    const requestTaskSchemasById = objectFromEntries(
      result.data.requestTaskSchemas.map((requestTaskSchema) => [requestTaskSchema.id, requestTaskSchema])
    );
    const requestTemplates = result.data.requestTemplates;
    const requestTemplatesById = objectFromEntries(
      requestTemplates.map((requestTemplate) => [requestTemplate.id, requestTemplate])
    );
    const _handleSubmit = (): void => {
      if (requestSchema == null) {
        return;
      }
      const initialSampleGroups = samples.map((sampleDetails: SampleDetails) => {
        const stageRunSample = makeStageRunSample(
          (sampleDetails.bioentityInfo || sampleDetails.batchInfo) as BioentityInfo | BatchInfo,
          sampleDetails.containerInfo
        );
        return updateSampleGroupWithSample(
          // Request schemas without sample group are filtered out by `isRequestSchemaValid` above
          restToGraphQL('SampleGroupSchema', requestSchema.sampleGroupSchema!),
          // @ts-expect-error
          makeSampleGroup(),
          requestSchema.sampleGroupSchema!.fields[0].name,
          stageRunSample
        );
      });

      const initialRequestProps = getInitialRequestProps(
        requestSchema,
        requestTemplatesById,
        requestTaskSchemasById
      );

      onRequestSubmit({
        ...initialRequestProps,
        // @ts-expect-error
        initialSampleGroups,
        // If the initial project id is already specified (by a request template) don't override it.
        initialProjectId: initialRequestProps.initialProjectId
          ? initialRequestProps.initialProjectId
          : rootFolderId,
      });
    };

    if (tooltipMessage) {
      notifications.info(tooltipMessage);
    }

    return (
      <Modal
        title="Select request schema"
        onRequestClose={onRequestClose}
        onRequestSubmit={_handleSubmit}
        buttons={[
          {
            text: 'Next',
            submit: true,
            style: ButtonStyle.PRIMARY,
            disabled: samples == null || requestSchema == null,
            key: 'next',
          },
        ]}
      >
        <RequestSchemaSelector
          selectedRequestSchema={requestSchema}
          onRequestSchemaChange={(requestSchema: RequestSchema) => {
            setRequestSchema(requestSchema);
          }}
          isRequestSchemaValid={(requestSchema) =>
            isRequestSchemaValid({requestSchema, sampleTypes, sampleSchemaIds})
          }
        />
      </Modal>
    );
  }
}

export default RequestSchemaSelectionModalDataConnector.connect(RequestSchemaSelectionModal);
